import { Box } from "@mui/material";
import { useAtomValue, useSetAtom } from "jotai";
import queryString from "query-string";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useCreateRelease from "../../../../../../api/releases/hooks/useCreateRelease";
import {
  DEFAULT_RELEASE_PRICE,
  DEFAULT_RELEASE_TRACK_PRICE,
} from "../../../../../../atoms/forms/editProductFormAtom";
import { PAGE_HASH } from "../../../../../../atoms/location/locationAtom";
import { supplyChainTokenAtom } from "../../../../../../atoms/supplychain/supplyChainTokenAtom";
import {
  activeUserAtom,
  activeUserIdAtom,
} from "../../../../../../atoms/user/activeUserAtom";
import { SUPPLY_CHAIN_ACTIVE } from "../../../../../../constants/generated";
import useHashBoolean from "../../../../../../hooks/reactRouterHooks/useHashBoolean";
import { useSupplyChainCredentialsMutation } from "../../../../../../hooks/supplyChainHooks/useSupplyChainCredentialsMutation";
import { useMediaQueryBreakpoint } from "../../../../../../hooks/useMediaQuery";
import {
  CreateReleaseFlow,
  DEFAULT_RELEASE_TITLE,
  ReleaseType,
} from "../../../../../../store/models/release";
import { getCreateReleaseRoute } from "../../../../../../store/utils/routeGetters";
import { FixedMinWidthButton } from "../../../../../core-ui/components/Button/FixedMinWidthButton";
import { ButtonVariant } from "../../../../../core-ui/components/Button/utils";
import { ResponsiveDialog } from "../../../../../core-ui/components/ResponsiveDialog/ResponsiveDialog";
import { Text } from "../../../../../core-ui/components/Text/Text";
import { TextStyleVariant } from "../../../../../core-ui/components/Text/TextUtils";
import { selectedCompletedProjectTracksAtom } from "../../../../CreateReleaseScreen/components/CreateDTCReleaseView/components/AddSongsView/atoms";
import { CreateReleaseCard } from "./CreateReleaseCard";

interface CreateReleaseModalProps {
  albumTitle?: string;
}

const RELEASE_PRODUCT_TYPES = [
  {
    type: CreateReleaseFlow.FREE,
    title: "Release for Free",
    description: "Share your music with everyone for free.",
  },
  {
    type: CreateReleaseFlow.DTC,
    title: "Sell Direct",
    description:
      "Directly sell your music to fans exclusively before it becomes available on streaming platforms.",
  },
];

export const CreateReleaseModal = ({ albumTitle }: CreateReleaseModalProps) => {
  const {
    isOpen: isCreateReleaseModalOpen,
    setHash: setCreateReleaseModalOpen,
  } = useHashBoolean({ value: PAGE_HASH.CREATE_RELEASE });
  const [selectedProductType, setSelectedProductType] =
    useState<CreateReleaseFlow>(CreateReleaseFlow.DTC);
  const { isDesktop } = useMediaQueryBreakpoint();
  const navigate = useNavigate();
  const { mutateAsync: createRelease, isPending: isCreateReleasePending } =
    useCreateRelease();
  const setSelectedTracks = useSetAtom(selectedCompletedProjectTracksAtom);
  const activeUserId = useAtomValue(activeUserIdAtom);
  const {
    mutateAsync: getSupplyChainCredentials,
    isPending: isCredentialsPending,
  } = useSupplyChainCredentialsMutation(activeUserId);
  const scToken = useAtomValue(supplyChainTokenAtom);

  const extraProductTypes = [
    {
      type: CreateReleaseFlow.DSP,
      title: "Release Globally",
      description:
        "Distribute your music globally to 100+ streaming platforms and online stores.",
    },
    {
      type: CreateReleaseFlow.DTC,
      title: "Both",
      description:
        "Directly sell your music to fans and schedule your music to release globally to 100+ streaming platforms and online stores.",
    },
  ];

  const user = useAtomValue(activeUserAtom); // Assuming user information is stored in activeUserIdAtom

  const availableProductTypes = user?.is_superuser
    ? [...RELEASE_PRODUCT_TYPES, ...extraProductTypes]
    : RELEASE_PRODUCT_TYPES;

  const handleCloseModal = useCallback(() => {
    // reset form when closing modal
    setSelectedProductType(CreateReleaseFlow.DTC);
    // reset selected tracks (on ScheduledProject overview page)
    setSelectedTracks({});
    setCreateReleaseModalOpen(false);
  }, [setCreateReleaseModalOpen, setSelectedProductType, setSelectedTracks]);

  const handleCreateRelease = useCallback(async () => {
    // Create or fetch Supply Chain auth tokens if supply chain integration is active
    if (!scToken && SUPPLY_CHAIN_ACTIVE) {
      await getSupplyChainCredentials();
    }

    const isDTC = selectedProductType === CreateReleaseFlow.DTC;
    const { release, product } = await createRelease({
      release_type: ReleaseType.ALBUM,
      title: albumTitle || DEFAULT_RELEASE_TITLE,
      ...(isDTC && { total_price: DEFAULT_RELEASE_PRICE }),
      ...(isDTC && { unit_price: DEFAULT_RELEASE_TRACK_PRICE }),
    });

    if (!release?.id) {
      toast.error(
        "Whoops! Something went wrong. Please try again or reach out to customer support.",
      );
      return;
    }

    const search = `?${queryString.stringify(
      { stepper: 0 },
      { skipEmptyString: true, skipNull: true },
    )}`;

    navigate({
      pathname: getCreateReleaseRoute(release.id, selectedProductType),
      search,
    });
  }, [
    createRelease,
    navigate,
    selectedProductType,
    albumTitle,
    getSupplyChainCredentials,
    scToken,
  ]);

  return (
    <ResponsiveDialog
      open={isCreateReleaseModalOpen}
      onClose={handleCloseModal}
      maxWidth="lg"
      contentProps={{
        children: (
          <Box boxSizing="border-box" padding={{ xs: 0, sm: 2, md: 4 }}>
            <Text variant={TextStyleVariant.S3} sx={{ textAlign: "center" }}>
              Choose your music release strategy below:
            </Text>
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              boxSizing="border-box"
              paddingY={4}
            >
              {availableProductTypes.map(({ type, title, description }) => (
                <CreateReleaseCard
                  key={type}
                  selected={selectedProductType === type}
                  onClick={() => setSelectedProductType(type)}
                  isDesktop={isDesktop}
                  title={title}
                  description={description}
                />
              ))}
            </Box>
            <Box display="flex" justifyContent="center" gap={2}>
              <FixedMinWidthButton
                variant={ButtonVariant.BLACK_OUTLINED_BUTTON_BLACK_HOVER}
                onClick={handleCloseModal}
              >
                Cancel
              </FixedMinWidthButton>
              <FixedMinWidthButton
                disabled={isCreateReleasePending || isCredentialsPending}
                loading={isCreateReleasePending || isCredentialsPending}
                onClick={handleCreateRelease}
              >
                Next
              </FixedMinWidthButton>
            </Box>
          </Box>
        ),
      }}
      titleProps={{ children: "How do you want to release your music?" }}
    />
  );
};
